import React, { useEffect, useCallback, useState, useRef } from 'react'
import PropTypes from 'prop-types'
import { FormInput, Margin, Text } from 'shared/components'
import { EditableFormPanel } from 'routes/shared/components'
import AsyncSelect from 'react-select/async'
import { components } from 'react-select'
import ReactDOM from 'react-dom'
import Select from 'react-select'

import { TEAM_OPTIONS, CATEGORY_OPTIONS, SUBCATEGORY_OPTIONS, CONDITION_OPTIONS } from './constants'
import { handleEventsMenuOpen, handleAthletesMenuOpen } from './menuOpen.helpers'
import * as styles from './styles'

// Utility for conditional logging - set to false in production
const DEBUG = false;
const log = (message, ...args) => {
  // No-op function to avoid console logs
};

const getOptionBackgroundColor = ({ isFocused, isSelected }) => {
  if (isSelected) {
    return '#3e3e3e'
  }

  if (isFocused) {
    return '#383838'
  }

  return '#2a2a2a'
}

// Common dropdown styles to avoid duplication
const commonDropdownStyles = {
  container: (base) => ({
    ...base,
    width: '100%',
  }),
  control: (base) => ({
    ...base,
    backgroundColor: '#2a2a2a',
    borderColor: '#3e3e3e',
    minHeight: '40px',
    height: '40px',
    boxShadow: 'none',
    borderRadius: '4px',
    '&:hover': {
      borderColor: '#4a4a4a'
    }
  }),
  menu: (base) => ({
    ...base,
    backgroundColor: '#2a2a2a',
    zIndex: 99999,
  }),
  menuList: (base) => ({
    ...base,
    maxHeight: '150px',
    padding: '4px 0'
  }),
  option: (base, { isFocused, isSelected }) => ({
    ...base,
    backgroundColor: getOptionBackgroundColor({ isFocused, isSelected }),
    color: '#ffffff',
    '&:active': {
      backgroundColor: '#3e3e3e'
    },
    fontFamily: 'Montserrat, Arial, Helvetica, sans-serif',
    fontSize: '10px'
  }),
  input: (base) => ({
    ...base,
    color: '#ffffff',
    margin: '0',
    padding: '0 4px',
    fontFamily: 'Montserrat, Arial, Helvetica, sans-serif',
    fontSize: '12px',
    minHeight: '28px'
  }),
  placeholder: (base) => ({
    ...base,
    color: '#757575',
    fontFamily: 'Montserrat, Arial, Helvetica, sans-serif',
    fontSize: '10px'
  }),
  singleValue: (base) => ({
    ...base,
    color: '#ffffff',
    fontFamily: 'Montserrat, Arial, Helvetica, sans-serif',
    fontSize: '10px'
  }),
  indicatorSeparator: (base) => ({
    ...base,
    backgroundColor: '#3e3e3e'
  }),
  dropdownIndicator: (base) => ({
    ...base,
    color: '#757575',
    padding: '8px',
    '&:hover': {
      color: '#ffffff'
    },
    cursor: 'pointer',
    fontSize: '14px',
    background: '#383838',
    borderRadius: '0 4px 4px 0'
  }),
  clearIndicator: (base) => ({
    ...base,
    color: '#757575',
    padding: '8px',
    '&:hover': {
      color: '#ffffff'
    },
    fontSize: '10px'
  }),
  valueContainer: (base) => ({
    ...base,
    padding: '0 8px',
    fontSize: '10px'
  })
};

// Custom Athlete Option component for better display
const AthleteOption = props => {
  const { data } = props;

  // Extract necessary data for display
  const firstName = data.name || '';
  const lastName = data.surname || '';
  const name = `${firstName} ${lastName}`.trim();
  const teamName = data.teamName || data.team?.name || '';

  return (
    <components.Option {...props}>
      <div style={{ display: 'flex', flexDirection: 'column' }}>
        <span style={{ fontWeight: 'bold' }}>{name || 'Unnamed Athlete'}</span>
        {teamName && (
          <span style={{ fontSize: '8px', opacity: 0.8 }}>{teamName}</span>
        )}
      </div>
    </components.Option>
  );
};

const athleteSelectDataShape = PropTypes.shape({
  name: PropTypes.string,
  surname: PropTypes.string,
  teamName: PropTypes.string,
  team: PropTypes.shape({
    name: PropTypes.string,
  }),
})

AthleteOption.propTypes = {
  data: athleteSelectDataShape.isRequired,
}

// Custom Athlete SingleValue component for selected athlete display
const AthleteSingleValue = props => {
  const { data } = props;

  // Extract necessary data for display
  const firstName = data.name || '';
  const lastName = data.surname || '';
  const name = `${firstName} ${lastName}`.trim();
  const teamName = data.teamName || data.team?.name || '';

  return (
    <components.SingleValue {...props}>
      <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.2 }}>
        <span>{name || 'Unnamed Athlete'}</span>
        {teamName && (
          <span style={{ fontSize: '8px', opacity: 0.8 }}>{teamName}</span>
        )}
      </div>
    </components.SingleValue>
  );
};

AthleteSingleValue.propTypes = {
  data: athleteSelectDataShape.isRequired,
}

const TEAM_BINARY_WINNER_OPTIONS = [
  { id: 'TEAM_A', name: 'TEAM_A' },
  { id: 'TEAM_B', name: 'TEAM_B' },
]

const resetAthleteSelectionState = ({
  setAthleteSelection,
  setAthleteInput,
  setAvailableAthletes,
  setFilteredAthletes,
}) => {
  setAthleteSelection(null)
  setAthleteInput('')
  setAvailableAthletes([])
  setFilteredAthletes([])
}

const resetBinaryWinnerState = ({ setEventTeams, setSelectedBinaryWinner, field }) => {
  setEventTeams([])
  setSelectedBinaryWinner(null)
  field.onChange({ target: { value: null } })
}

const updateBinaryWinnerOptions = ({
  selectedOption,
  setEventTeams,
  setSelectedBinaryWinner,
  field,
}) => {
  if (selectedOption?.teamOne && selectedOption?.teamTwo) {
    log('Setting simplified team options:', TEAM_BINARY_WINNER_OPTIONS)
    setEventTeams(TEAM_BINARY_WINNER_OPTIONS)
    setSelectedBinaryWinner(null)
    field.onChange({ target: { value: null } })
    return
  }

  resetBinaryWinnerState({
    setEventTeams,
    setSelectedBinaryWinner,
    field,
  })
}

const applyLoadedEvents = ({
  selectedOption,
  events,
  isMountedRef,
  setAvailableEvents,
  setFilteredEvents,
  eventsLoadedRef,
  setRenderKey,
}) => {
  if (!isMountedRef.current) {
    return
  }

  const validEvents = events || []

  setAvailableEvents(validEvents)
  setFilteredEvents(validEvents)
  eventsLoadedRef.current = { id: selectedOption.id, loaded: true }
  setRenderKey((prev) => prev + 1)
}

const clearLoadedEvents = ({ isMountedRef, setAvailableEvents, setFilteredEvents }) => {
  if (!isMountedRef.current) {
    return
  }

  setAvailableEvents([])
  setFilteredEvents([])
}

const applyLoadedAthletes = ({
  eventId,
  athletes,
  isMountedRef,
  setAvailableAthletes,
  setFilteredAthletes,
  athletesLoadedRef,
  athletesCacheRef,
  setRenderKey,
}) => {
  if (!isMountedRef.current) {
    return
  }

  const validAthletes = athletes || []
  log(`Loaded ${validAthletes.length} athletes for event ${eventId}`)

  setAvailableAthletes(validAthletes)
  setFilteredAthletes(validAthletes)
  athletesLoadedRef.current = { id: eventId, loaded: true }
  athletesCacheRef.current.set(eventId, validAthletes)
  setRenderKey((prev) => prev + 1)
}

const clearLoadedAthletes = ({ isMountedRef, setAvailableAthletes, setFilteredAthletes }) => {
  if (!isMountedRef.current) {
    return
  }

  setAvailableAthletes([])
  setFilteredAthletes([])
}

const EditTriggerFormComponent = ({
  form,
  header,
  getOptionLabel,
  getOptionValue,
  getEventLabel,
  getEventValue,
  getAthleteLabel,
  getAthleteValue,
  loadEvents,
  loadAthletes,
  loadTriggerConditions,
  loadTriggerCategories,
  loadTriggerSubcategories,
  loadTournaments,
  onChangeTournament,
  onChangeEvent,
  onChangeAthlete,
  onSubmitRequest,
}) => {
  // State to track input values for tournament dropdown
  const [inputValue, setInputValue] = useState('');
  const [eventInput, setEventInput] = useState('');
  const [athleteInput, setAthleteInput] = useState('');

  // Local state to track the tournament selection for UI rendering
  const [tournamentSelection, setTournamentSelection] = useState(form.fields.tournamentId.value);
  const [eventSelection, setEventSelection] = useState(form.fields.event.value);
  const [athleteSelection, setAthleteSelection] = useState(form.fields.athleteId.value);

  // State to store available events for the selected tournament
  const [availableEvents, setAvailableEvents] = useState([]);
  const [availableAthletes, setAvailableAthletes] = useState([]);

  // State to store filtered events based on search
  const [filteredEvents, setFilteredEvents] = useState([]);
  const [filteredAthletes, setFilteredAthletes] = useState([]);

  // Render key to force re-render when needed
  const [renderKey, setRenderKey] = useState(0);

  // Create refs to track loaded data with IDs to check for stale data
  const eventsLoadedRef = useRef({ id: null, loaded: false });
  const athletesLoadedRef = useRef({ id: null, loaded: false });

  // Add a cache for athletes by event ID to prevent repeated API calls
  const athletesCacheRef = useRef(new Map());

  // Track if component is mounted
  const isMountedRef = useRef(true);

  // Add state to directly store the currently selected event data with athletes
  const [currentEventData, setCurrentEventData] = useState(null);

  // Create a portal target for dropdown menus
  const [menuPortalTarget, setMenuPortalTarget] = useState(null);

  // Add state to store event teams for the Binary Winner dropdown
  const [eventTeams, setEventTeams] = useState([]);

  // Add separate local state for binary winner selection
  const [selectedBinaryWinner, setSelectedBinaryWinner] = useState(null);

  // Effect to sync form value with local state
  useEffect(() => {
    if (form.fields.binaryWinner.value !== selectedBinaryWinner) {
      setSelectedBinaryWinner(form.fields.binaryWinner.value);
    }
  }, [form.fields.binaryWinner.value]);

  useEffect(() => {
    // Set the portal target to the document body
    setMenuPortalTarget(document.body);

    return () => {
      // Clean up on unmount
      setMenuPortalTarget(null);
    };
  }, []);

  // Cleanup function to prevent memory leaks
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Function to load tournaments with debounced search
  const debouncedLoadTournaments = useCallback((inputText) => {
    // Don't proceed if component is unmounted
    if (!isMountedRef.current) {
      return Promise.resolve([]);
    }

    if (!inputText || inputText.length < 2) {
      return Promise.resolve([]);
    }

    return loadTournaments(inputText)
      .then(results => {
        // Check if component is still mounted before proceeding
        if (!isMountedRef.current) {
          return [];
        }

        return results || [];
      })
      .catch(err => {
        log('Tournament search error:', err);
        return [];
      });
  }, [loadTournaments, isMountedRef]);

  // Function to load events filtered by tournament
  const loadEventOptions = useCallback((inputText) => {
    log('Loading event options, search:', inputText);

    // If no tournament is selected, return empty results
    if (!tournamentSelection) {
      return Promise.resolve([]);
    }

    // If no events available but tournament selected, load them
    if (availableEvents.length === 0 || eventsLoadedRef.current.id !== tournamentSelection.id) {
      return loadEvents(inputText, tournamentSelection.id)
        .then(events => {
          const validEvents = events || [];
          // Update state with loaded events
          if (isMountedRef.current) {
            // Make sure we store the complete event objects with team and athlete information
            log(`Loaded ${validEvents.length} events with complete data`);

            // Log the first event's athlete IDs if available
            if (validEvents.length > 0) {
              const firstEvent = validEvents[0];
              const teamOneAthletes = firstEvent.teamOne?.athletesIds || [];
              const teamTwoAthletes = firstEvent.teamTwo?.athletesIds || [];
              log(`First event (${firstEvent.id}) has ${teamOneAthletes.length + teamTwoAthletes.length} athletes: ${teamOneAthletes.length} in teamOne, ${teamTwoAthletes.length} in teamTwo`);
            }

            setAvailableEvents(validEvents);
            setFilteredEvents(validEvents);
            eventsLoadedRef.current = { id: tournamentSelection.id, loaded: true };
          }
          return validEvents;
        })
        .catch(() => []);
    }

    // Return client-side filtered events
    return Promise.resolve([...availableEvents]);
  }, [availableEvents, tournamentSelection, loadEvents, isMountedRef]);

  // Function to load athletes filtered by event
  const loadAthleteOptions = useCallback((inputText) => {
    log('Loading athlete options, search:', inputText);

    // If no event is selected, return empty results
    if (!eventSelection) {
      return Promise.resolve([]);
    }

    // Check if we have cached athletes for this event
    const eventId = eventSelection.id;
    const cachedAthletes = athletesCacheRef.current.get(eventId);

    // If we have cached athletes, filter them by search text and return immediately
    if (cachedAthletes) {
      log(`Using ${cachedAthletes.length} cached athletes for event ${eventId}`);
      let filteredAthletes = [...cachedAthletes];

      // Apply client-side filtering if there's a search term
      if (inputText) {
        const searchLower = inputText.toLowerCase();
        filteredAthletes = filteredAthletes.filter(athlete => {
          const firstName = athlete.name || '';
          const lastName = athlete.surname || '';
          const fullName = `${firstName} ${lastName}`.trim().toLowerCase();
          return fullName.includes(searchLower);
        });
      }

      return Promise.resolve(filteredAthletes);
    }

    // If no athletes available but event selected, load them
    if (availableAthletes.length === 0 || athletesLoadedRef.current.id !== eventSelection.id) {
      return loadAthletes('', eventSelection.id)
        .then(athletes => {
          log(`Retrieved ${athletes?.length || 0} athletes for event ${eventId}`);

          const validAthletes = athletes || [];
          // Update state with loaded athletes
          if (isMountedRef.current) {
            setAvailableAthletes(validAthletes);
            setFilteredAthletes(validAthletes);
            athletesLoadedRef.current = { id: eventSelection.id, loaded: true };

            // Cache the athletes for this event
            athletesCacheRef.current.set(eventSelection.id, validAthletes);

            // Log athlete IDs to help with debugging
            if (validAthletes.length > 0) {
              const athleteIds = validAthletes.map(a => a.id).slice(0, 5);
              log(`First few athlete IDs: ${athleteIds.join(', ')}${validAthletes.length > 5 ? '...' : ''}`);
            }

            // If we got no athletes, log a warning
            if (validAthletes.length === 0) {
              log(`Warning: No athletes found for event ${eventId}. Check the event data and athlete fetching logic.`);
            }
          }
          return validAthletes;
        })
        .catch(error => {
          log(`Error loading athletes for event ${eventId}:`, error);
          return [];
        });
    }

    // Return client-side filtered athletes
    const athletes = [...availableAthletes];

    // Apply client-side filtering if there's a search term
    if (inputText) {
      const searchLower = inputText.toLowerCase();
      return Promise.resolve(athletes.filter(athlete => {
        const firstName = athlete.name || '';
        const lastName = athlete.surname || '';
        const fullName = `${firstName} ${lastName}`.trim().toLowerCase();
        return fullName.includes(searchLower);
      }));
    }

    return Promise.resolve(athletes);
  }, [availableAthletes, eventSelection, loadAthletes, isMountedRef]);

  // Create a stable reference for an option label cache to prevent excessive re-renders
  const optionLabelCache = useRef(new Map());

  // Memoize option rendering functions to reduce re-renders
  const getTournamentOptionLabel = useCallback((option) => {
    // Use cached value if available to prevent re-renders
    if (option.id && optionLabelCache.current.has(option.id)) {
      return optionLabelCache.current.get(option.id);
    }

    // Calculate the label
    const label = option.name || option.tournamentName || 'Unnamed tournament';

    // Store in cache
    if (option.id) {
      optionLabelCache.current.set(option.id, label);
    }

    return label;
  }, []);

  const getTournamentOptionValue = useCallback((option) => {
    return option.id?.toString();
  }, []);

  // Handle tournament selection changes
  const handleTournamentChange = useCallback((selectedOption) => {
    log('Tournament selection changed:', selectedOption?.id);

    // Update local state first for immediate UI feedback
    setTournamentSelection(selectedOption);

    // Reset event dropdown state
    setEventSelection(null);
    setEventInput('');

    resetAthleteSelectionState({
      setAthleteSelection,
      setAthleteInput,
      setAvailableAthletes,
      setFilteredAthletes,
    })

    // Clear available events until the new ones load
    setAvailableEvents([]);
    setFilteredEvents([]);

    // Then notify parent
    onChangeTournament(selectedOption);

    // Reset loaded refs
    eventsLoadedRef.current = { id: null, loaded: false };
    athletesLoadedRef.current = { id: null, loaded: false };

    // If a tournament is selected, immediately load its events
    if (selectedOption?.id) {
      // Set key state to force re-render dropdown
      setRenderKey(prev => prev + 1);

      // Load events with a slight delay to ensure proper state updates
      setTimeout(() => {
        if (isMountedRef.current) {
          loadEvents('', selectedOption.id)
            .then((events) =>
              applyLoadedEvents({
                selectedOption,
                events,
                isMountedRef,
                setAvailableEvents,
                setFilteredEvents,
                eventsLoadedRef,
                setRenderKey,
              })
            )
            .catch((error) => {
              log('Error loading events for tournament:', error)
              clearLoadedEvents({
                isMountedRef,
                setAvailableEvents,
                setFilteredEvents,
              })
            });
        }
      }, 100);
    }
  }, [onChangeTournament, loadEvents, form.fields.binaryWinner]);

  // Add a click handler for the event dropdown to ensure events are loaded
  const eventDropdownRef = useRef(null);

  // Function to handle click on the event dropdown element
  const handleEventDropdownClick = useCallback(() => {
    handleEventsMenuOpen({
      eventsLoadedRef,
      tournamentSelection,
      loadEvents,
      isMountedRef,
      setAvailableEvents,
      setFilteredEvents,
      setRenderKey,
      log: (message, error) => log('Error loading events on dropdown click:', error || message),
    })
  }, [tournamentSelection, loadEvents, isMountedRef])

  const handleEventMenuOpen = useCallback(() => {
    handleEventsMenuOpen({
      eventsLoadedRef,
      tournamentSelection,
      loadEvents,
      isMountedRef,
      setAvailableEvents,
      setFilteredEvents,
      setRenderKey,
      log,
    })
  }, [tournamentSelection, loadEvents, isMountedRef])

  const handleAthleteMenuOpen = useCallback(() => {
    handleAthletesMenuOpen({
      eventSelection,
      athletesCacheRef,
      loadAthletes,
      isMountedRef,
      setAvailableAthletes,
      setFilteredAthletes,
      athletesLoadedRef,
      log,
    })
  }, [eventSelection, loadAthletes, isMountedRef])

  // Improve event selection to save the complete event data and extract teams
  const handleEventChange = useCallback((selectedOption) => {
    log('Event selection changed:', selectedOption?.id);

    // Update local state for immediate UI feedback
    setEventSelection(selectedOption);

    resetAthleteSelectionState({
      setAthleteSelection,
      setAthleteInput,
      setAvailableAthletes,
      setFilteredAthletes,
    })

    // Save the complete event data for athlete loading
    setCurrentEventData(selectedOption);

    updateBinaryWinnerOptions({
      selectedOption,
      setEventTeams,
      setSelectedBinaryWinner,
      field: form.fields.binaryWinner,
    })

    // Notify parent about event change
    onChangeEvent(selectedOption);

    // Reset loaded refs
    athletesLoadedRef.current = { id: null, loaded: false };

    // If an event is selected, immediately load its athletes
    if (selectedOption?.id) {
      // Check if we have cached athletes for this event
      const cachedAthletes = athletesCacheRef.current.get(selectedOption.id);

      if (cachedAthletes) {
        // Use cached athletes if available
        log(`Using ${cachedAthletes.length} cached athletes for event ${selectedOption.id}`);
        setAvailableAthletes(cachedAthletes);
        setFilteredAthletes(cachedAthletes);
        athletesLoadedRef.current = { id: selectedOption.id, loaded: true };
        setRenderKey(prev => prev + 1);
        return;
      }

      // Set key state to force re-render dropdown
      setRenderKey(prev => prev + 1);

      // Log the event details to help debug athlete loading
      if (selectedOption.teamOne || selectedOption.teamTwo) {
        const teamOneAthletes = selectedOption.teamOne?.athletesIds || [];
        const teamTwoAthletes = selectedOption.teamTwo?.athletesIds || [];
        log(`Selected event (${selectedOption.id}) has ${teamOneAthletes.length + teamTwoAthletes.length} athletes: ${teamOneAthletes.length} in teamOne, ${teamTwoAthletes.length} in teamTwo`);
      }

      // Load athletes with a slight delay to ensure proper state updates
      setTimeout(() => {
        if (isMountedRef.current) {
          loadAthletes('', selectedOption.id)
            .then((athletes) =>
              applyLoadedAthletes({
                eventId: selectedOption.id,
                athletes,
                isMountedRef,
                setAvailableAthletes,
                setFilteredAthletes,
                athletesLoadedRef,
                athletesCacheRef,
                setRenderKey,
              })
            )
            .catch((error) => {
              log('Error loading athletes for event:', error)
              clearLoadedAthletes({
                isMountedRef,
                setAvailableAthletes,
                setFilteredAthletes,
              })
            });
        }
      }, 100);
    }
  }, [onChangeEvent, loadAthletes, isMountedRef, form.fields.binaryWinner]);

  // Handle binary winner selection change separately, making sure to log and handle correctly
  const handleBinaryWinnerChange = useCallback((selectedOption) => {
    log('Binary winner selected:', selectedOption);

    // Update local state first
    setSelectedBinaryWinner(selectedOption);

    // Then update form state
    setTimeout(() => {
      form.fields.binaryWinner.onChange({ target: { value: selectedOption } });
    }, 0);
  }, [form.fields.binaryWinner]);

  // Simple function to get team labels
  const getTeamLabel = useCallback((option) => {
    if (!option) return '';
    return option.name || '';
  }, []);

  // Simple value accessor for teams
  const getTeamValue = useCallback((option) => {
    if (!option) return '';
    return option.id || '';
  }, []);

  // Handle athlete selection changes
  const handleAthleteChange = useCallback((selectedOption) => {
    // Update local state for immediate UI feedback
    setAthleteSelection(selectedOption);

    // Update form value using the onChangeAthlete callback
    onChangeAthlete(selectedOption);
  }, [onChangeAthlete]);

  // Handle input changes in the tournament search
  const handleTournamentInput = useCallback((text) => {
    setInputValue(text);
    return text;
  }, []);

  // Handle input changes in the event search
  const handleEventInput = useCallback((text) => {
    setEventInput(text);
    return text;
  }, []);

  // Handle input changes in the athlete search
  const handleAthleteInput = useCallback((text) => {
    setAthleteInput(text);
    return text;
  }, []);

  // Memoize the no options message function
  const noOptionsMessage = useCallback(({ inputValue: text }) => {
    if (!text || text.length < 2) {
      return <span style={{ fontFamily: 'Montserrat, Arial, Helvetica, sans-serif', fontSize: '10px' }}>Type at least 2 characters to search</span>;
    }
    return <span style={{ fontFamily: 'Montserrat, Arial, Helvetica, sans-serif', fontSize: '10px' }}>Loading...</span>;
  }, []);

  // No events message when tournament is not selected or no events are found
  const noEventsMessage = useCallback(({ inputValue: text }) => {
    if (!tournamentSelection) {
      return <span style={{ fontFamily: 'Montserrat, Arial, Helvetica, sans-serif', fontSize: '10px' }}>Select a tournament first</span>;
    }

    if (availableEvents.length === 0) {
      return <span style={{ fontFamily: 'Montserrat, Arial, Helvetica, sans-serif', fontSize: '10px' }}>No events found for this tournament</span>;
    }

    if (filteredEvents.length === 0) {
      return <span style={{ fontFamily: 'Montserrat, Arial, Helvetica, sans-serif', fontSize: '10px' }}>No matching events found</span>;
    }

    return <span style={{ fontFamily: 'Montserrat, Arial, Helvetica, sans-serif', fontSize: '10px' }}>Type to filter events</span>;
  }, [tournamentSelection, availableEvents, filteredEvents]);

  // No athletes message when event is not selected or no athletes are found
  const noAthletesMessage = useCallback(({ inputValue: text }) => {
    if (!eventSelection) {
      return <span style={{ fontFamily: 'Montserrat, Arial, Helvetica, sans-serif', fontSize: '10px' }}>Select an event first</span>;
    }

    if (availableAthletes.length === 0) {
      return <span style={{ fontFamily: 'Montserrat, Arial, Helvetica, sans-serif', fontSize: '10px' }}>No athletes found for this event</span>;
    }

    if (filteredAthletes.length === 0) {
      return <span style={{ fontFamily: 'Montserrat, Arial, Helvetica, sans-serif', fontSize: '10px' }}>No matching athletes found</span>;
    }

    return <span style={{ fontFamily: 'Montserrat, Arial, Helvetica, sans-serif', fontSize: '10px' }}>Type to filter athletes</span>;
  }, [eventSelection, availableAthletes, filteredAthletes]);

  // Load tournament options
  const loadTournamentOptions = useCallback((inputText) => {
    return debouncedLoadTournaments(inputText);
  }, [debouncedLoadTournaments]);

  // Handle force clear tournament
  const handleClearTournament = useCallback(() => {
    log('Force clearing tournament');

    // Directly clear local selection state
    setTournamentSelection(null);

    // Directly clear form state
    form.fields.tournamentId.onChange({ target: { value: null } });

    // Reset input value
    setInputValue('');

    // Force re-render by incrementing the key
    setRenderKey(prev => prev + 1);
  }, [form.fields.tournamentId]);

  return (
    <EditableFormPanel header={header} form={form} onSubmit={onSubmitRequest}>
      {(editable) => {
        const disabled = !editable

        if (DEBUG) {
          log('Render state:', {
            tournamentSelection: tournamentSelection ? `ID: ${tournamentSelection.id}` : 'none',
            availableEvents: availableEvents.length,
            filteredEvents: filteredEvents.length,
            eventSelection: eventSelection ? `ID: ${eventSelection.id}` : 'none',
            eventTeams,
            selectedBinaryWinner,
            formBinaryWinner: form.fields.binaryWinner.value
          });
        }

        return (
          <>
            <Margin bottom={1} css={styles.row}>
              <Text as="label" size={11} style={{ width: '100px', textAlign: 'left' }}>
                Tournament
              </Text>
              <div style={{ width: '180px', marginLeft: '2px' }}>
                <AsyncSelect
                  key={`tournament-select-${renderKey}`}
                  id="direct-tournament-select"
                  cacheOptions={false}
                  defaultOptions={false}
                  isSearchable={true}
                  loadOptions={loadTournamentOptions}
                  getOptionLabel={getTournamentOptionLabel}
                  getOptionValue={getTournamentOptionValue}
                  onChange={handleTournamentChange}
                  onInputChange={handleTournamentInput}
                  isClearable={true}
                  placeholder="Search tournaments..."
                  noOptionsMessage={noOptionsMessage}
                  value={tournamentSelection}
                  menuIsOpen={inputValue && inputValue.length >= 2 ? true : undefined}
                  isDisabled={disabled}
                  styles={{
                    container: (base) => ({
                      ...base,
                      width: '100%',
                    }),
                    control: (base) => ({
                      ...base,
                      backgroundColor: '#2a2a2a',
                      borderColor: '#3e3e3e',
                      minHeight: '40px',
                      height: '40px',
                      boxShadow: 'none',
                      borderRadius: '4px',
                      '&:hover': {
                        borderColor: '#4a4a4a'
                      }
                    }),
                    menu: (base) => ({
                      ...base,
                      backgroundColor: '#2a2a2a',
                      zIndex: 9999,
                    }),
                    menuList: (base) => ({
                      ...base,
                      maxHeight: '150px',
                      padding: '4px 0'
                    }),
                    option: (base, { isFocused, isSelected }) => ({
                      ...base,
                      backgroundColor: getOptionBackgroundColor({ isFocused, isSelected }),
                      color: '#ffffff',
                      '&:active': {
                        backgroundColor: '#3e3e3e'
                      },
                      fontFamily: 'Montserrat, Arial, Helvetica, sans-serif',
                      fontSize: '10px'
                    }),
                    input: (base) => ({
                      ...base,
                      color: '#ffffff',
                      margin: '0',
                      padding: '0',
                      fontFamily: 'Montserrat, Arial, Helvetica, sans-serif',
                      fontSize: '10px'
                    }),
                    placeholder: (base) => ({
                      ...base,
                      color: '#757575',
                      fontFamily: 'Montserrat, Arial, Helvetica, sans-serif',
                      fontSize: '10px'
                    }),
                    singleValue: (base) => ({
                      ...base,
                      color: '#ffffff',
                      fontFamily: 'Montserrat, Arial, Helvetica, sans-serif',
                      fontSize: '10px'
                    }),
                    indicatorSeparator: (base) => ({
                      ...base,
                      backgroundColor: '#3e3e3e'
                    }),
                    dropdownIndicator: (base) => ({
                      ...base,
                      color: '#757575',
                      padding: '8px',
                      '&:hover': {
                        color: '#ffffff'
                      },
                      fontSize: '10px'
                    }),
                    clearIndicator: (base) => ({
                      ...base,
                      color: '#757575',
                      padding: '8px',
                      '&:hover': {
                        color: '#ffffff'
                      },
                      fontSize: '10px'
                    }),
                    valueContainer: (base) => ({
                      ...base,
                      padding: '0 8px',
                      fontSize: '10px'
                    })
                  }}
                />
              </div>
            </Margin>
            <Margin bottom={1} css={styles.row}>
              <Text as="label" size={11} style={{ width: '100px', textAlign: 'left' }}>
                Event
              </Text>
              <div style={{ width: '180px', marginLeft: '2px' }} onClick={handleEventDropdownClick} ref={eventDropdownRef}>
                <AsyncSelect
                  key={`event-select-${renderKey}`}
                  id="event-select"
                  cacheOptions={true}
                  defaultOptions={availableEvents.length > 0}
                  isSearchable={true}
                  loadOptions={loadEventOptions}
                  filterOption={(option, inputValue) => {
                    if (!inputValue) return true;
                    const matchName = option.data.matchName || '';
                    return matchName.toLowerCase().includes(inputValue.toLowerCase());
                  }}
                  getOptionLabel={getEventLabel}
                  getOptionValue={getEventValue}
                  onChange={handleEventChange}
                  onInputChange={handleEventInput}
                  isClearable={true}
                  placeholder={tournamentSelection ? "Select an event..." : "Select tournament first..."}
                  noOptionsMessage={noEventsMessage}
                  value={eventSelection}
                  isDisabled={disabled || !tournamentSelection}
                  styles={{
                    container: (base) => ({
                      ...base,
                      width: '100%',
                    }),
                    control: (base) => ({
                      ...base,
                      backgroundColor: '#2a2a2a',
                      borderColor: '#3e3e3e',
                      minHeight: '40px',
                      height: '40px',
                      boxShadow: 'none',
                      borderRadius: '4px',
                      '&:hover': {
                        borderColor: '#4a4a4a'
                      }
                    }),
                    menu: (base) => ({
                      ...base,
                      backgroundColor: '#2a2a2a',
                      zIndex: 9999,
                    }),
                    menuList: (base) => ({
                      ...base,
                      maxHeight: '150px',
                      padding: '4px 0'
                    }),
                    option: (base, { isFocused, isSelected }) => ({
                      ...base,
                      backgroundColor: getOptionBackgroundColor({ isFocused, isSelected }),
                      color: '#ffffff',
                      '&:active': {
                        backgroundColor: '#3e3e3e'
                      },
                      fontFamily: 'Montserrat, Arial, Helvetica, sans-serif',
                      fontSize: '10px'
                    }),
                    input: (base) => ({
                      ...base,
                      color: '#ffffff',
                      margin: '0',
                      padding: '0',
                      fontFamily: 'Montserrat, Arial, Helvetica, sans-serif',
                      fontSize: '10px'
                    }),
                    placeholder: (base) => ({
                      ...base,
                      color: '#757575',
                      fontFamily: 'Montserrat, Arial, Helvetica, sans-serif',
                      fontSize: '10px'
                    }),
                    singleValue: (base) => ({
                      ...base,
                      color: '#ffffff',
                      fontFamily: 'Montserrat, Arial, Helvetica, sans-serif',
                      fontSize: '10px'
                    }),
                    indicatorSeparator: (base) => ({
                      ...base,
                      backgroundColor: '#3e3e3e'
                    }),
                    dropdownIndicator: (base) => ({
                      ...base,
                      color: '#757575',
                      padding: '8px',
                      '&:hover': {
                        color: '#ffffff'
                      },
                      fontSize: '10px'
                    }),
                    clearIndicator: (base) => ({
                      ...base,
                      color: '#757575',
                      padding: '8px',
                      '&:hover': {
                        color: '#ffffff'
                      },
                      fontSize: '10px'
                    }),
                    valueContainer: (base) => ({
                      ...base,
                      padding: '0 8px',
                      fontSize: '10px'
                    })
                  }}
                  onMenuOpen={handleEventMenuOpen}
                />
              </div>
            </Margin>
            <Margin bottom={1} css={styles.row}>
              <Text as="label" size={11} style={{ width: '100px', textAlign: 'left' }}>
                Athlete
              </Text>
              <div
                style={{
                  width: '180px',
                  position: 'relative',
                  zIndex: 100, // Higher z-index for the container
                  marginLeft: '2px'
                }}
              >
                <AsyncSelect
                  key={`athlete-select-${renderKey}`}
                  id="athlete-select"
                  cacheOptions={true}
                  defaultOptions={true}
                  isSearchable={true}
                  loadOptions={loadAthleteOptions}
                  filterOption={(option, inputValue) => {
                    if (!inputValue) return true;
                    if (!option.data || typeof option.data !== 'object') return false;
                    const firstName = option.data.name || '';
                    const lastName = option.data.surname || '';
                    const fullName = `${firstName} ${lastName}`.trim().toLowerCase();
                    return fullName.includes(inputValue.toLowerCase());
                  }}
                  getOptionLabel={getAthleteLabel}
                  getOptionValue={getAthleteValue}
                  onChange={handleAthleteChange}
                  onInputChange={handleAthleteInput}
                  isClearable={true}
                  placeholder={eventSelection ? "Select an athlete..." : "Select event first..."}
                  noOptionsMessage={noAthletesMessage}
                  value={athleteSelection}
                  isDisabled={disabled || !eventSelection}
                  // Force the menu to be attached to the body and have an extremely high z-index
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                  menuPlacement="auto"
                  classNamePrefix="athlete-select"
                  // Use custom components for better athlete display
                  components={{
                    Option: AthleteOption,
                    SingleValue: AthleteSingleValue
                  }}
                  // Make sure we load athletes on menu open
                  onMenuOpen={handleAthleteMenuOpen}
                  styles={{
                    container: (base) => ({
                      ...base,
                      width: '100%',
                    }),
                    control: (base) => ({
                      ...base,
                      backgroundColor: '#2a2a2a',
                      borderColor: '#3e3e3e',
                      minHeight: '40px',
                      height: '40px',
                      boxShadow: 'none',
                      borderRadius: '4px',
                      '&:hover': {
                        borderColor: '#4a4a4a'
                      }
                    }),
                    // The key fixes - portaling menu to body with extremely high z-index
                    menuPortal: (base) => ({
                      ...base,
                      zIndex: 9999999,
                    }),
                    menu: (base) => ({
                      ...base,
                      backgroundColor: '#2a2a2a',
                      zIndex: 9999,
                    }),
                    menuList: (base) => ({
                      ...base,
                      maxHeight: '150px',
                      padding: '4px 0'
                    }),
                    option: (base, { isFocused, isSelected }) => ({
                      ...base,
                      backgroundColor: getOptionBackgroundColor({ isFocused, isSelected }),
                      color: '#ffffff',
                      '&:active': {
                        backgroundColor: '#3e3e3e'
                      },
                      fontFamily: 'Montserrat, Arial, Helvetica, sans-serif',
                      fontSize: '10px'
                    }),
                    input: (base) => ({
                      ...base,
                      color: '#ffffff',
                      margin: '0',
                      padding: '0',
                      fontFamily: 'Montserrat, Arial, Helvetica, sans-serif',
                      fontSize: '10px'
                    }),
                    placeholder: (base) => ({
                      ...base,
                      color: '#757575',
                      fontFamily: 'Montserrat, Arial, Helvetica, sans-serif',
                      fontSize: '10px'
                    }),
                    singleValue: (base) => ({
                      ...base,
                      color: '#ffffff',
                      fontFamily: 'Montserrat, Arial, Helvetica, sans-serif',
                      fontSize: '10px'
                    }),
                    indicatorSeparator: (base) => ({
                      ...base,
                      backgroundColor: '#3e3e3e'
                    }),
                    dropdownIndicator: (base) => ({
                      ...base,
                      color: '#757575',
                      padding: '8px',
                      '&:hover': {
                        color: '#ffffff'
                      },
                      fontSize: '10px'
                    }),
                    clearIndicator: (base) => ({
                      ...base,
                      color: '#757575',
                      padding: '8px',
                      '&:hover': {
                        color: '#ffffff'
                      },
                      fontSize: '10px'
                    }),
                    valueContainer: (base) => ({
                      ...base,
                      padding: '0 8px',
                      fontSize: '10px'
                    })
                  }}
                />
              </div>
            </Margin>
            <Margin bottom={1} css={styles.row}>
              <Text as="label" size={11} style={{ width: '100px', textAlign: 'left' }}>
                Category
              </Text>
              <div style={{ width: '180px', marginLeft: '2px' }}>
                <Select
                  options={CATEGORY_OPTIONS}
                  getOptionLabel={getOptionLabel}
                  getOptionValue={getOptionValue}
                  isDisabled={disabled}
                  placeholder="Categories"
                  onChange={(selectedOption) => {
                    form.fields.category.onChange({ target: { value: selectedOption } });
                  }}
                  value={form.fields.category.value}
                  styles={{
                    container: (base) => ({
                      ...base,
                      width: '100%',
                    }),
                    control: (base) => ({
                      ...base,
                      backgroundColor: '#2a2a2a',
                      borderColor: '#3e3e3e',
                      minHeight: '40px',
                      height: '40px',
                      boxShadow: 'none',
                      borderRadius: '4px',
                      '&:hover': {
                        borderColor: '#4a4a4a'
                      }
                    }),
                    menu: (base) => ({
                      ...base,
                      backgroundColor: '#2a2a2a',
                      zIndex: 9999,
                    }),
                    menuList: (base) => ({
                      ...base,
                      maxHeight: '150px',
                      padding: '4px 0'
                    }),
                    option: (base, { isFocused, isSelected }) => ({
                      ...base,
                      backgroundColor: getOptionBackgroundColor({ isFocused, isSelected }),
                      color: '#ffffff',
                      '&:active': {
                        backgroundColor: '#3e3e3e'
                      },
                      fontFamily: 'Montserrat, Arial, Helvetica, sans-serif',
                      fontSize: '10px'
                    }),
                    input: (base) => ({
                      ...base,
                      color: '#ffffff',
                      margin: '0',
                      padding: '0',
                      fontFamily: 'Montserrat, Arial, Helvetica, sans-serif',
                      fontSize: '10px'
                    }),
                    placeholder: (base) => ({
                      ...base,
                      color: '#757575',
                      fontFamily: 'Montserrat, Arial, Helvetica, sans-serif',
                      fontSize: '10px'
                    }),
                    singleValue: (base) => ({
                      ...base,
                      color: '#ffffff',
                      fontFamily: 'Montserrat, Arial, Helvetica, sans-serif',
                      fontSize: '10px'
                    }),
                    indicatorSeparator: (base) => ({
                      ...base,
                      backgroundColor: '#3e3e3e'
                    }),
                    dropdownIndicator: (base) => ({
                      ...base,
                      color: '#757575',
                      padding: '8px',
                      '&:hover': {
                        color: '#ffffff'
                      },
                      fontSize: '10px'
                    }),
                    clearIndicator: (base) => ({
                      ...base,
                      color: '#757575',
                      padding: '8px',
                      '&:hover': {
                        color: '#ffffff'
                      },
                      fontSize: '10px'
                    }),
                    valueContainer: (base) => ({
                      ...base,
                      padding: '0 8px',
                      fontSize: '10px'
                    })
                  }}
                />
              </div>
            </Margin>
            <Margin bottom={1} css={styles.row}>
              <Text as="label" size={11} style={{ width: '100px', textAlign: 'left' }}>
                Subcategory
              </Text>
              <div style={{ width: '180px', marginLeft: '2px' }}>
                <Select
                  options={SUBCATEGORY_OPTIONS}
                  getOptionLabel={getOptionLabel}
                  getOptionValue={getOptionValue}
                  isDisabled={disabled}
                  placeholder="Subcategories"
                  onChange={(selectedOption) => {
                    form.fields.subCategory.onChange({ target: { value: selectedOption } });
                  }}
                  value={form.fields.subCategory.value}
                  styles={{
                    container: (base) => ({
                      ...base,
                      width: '100%',
                    }),
                    control: (base) => ({
                      ...base,
                      backgroundColor: '#2a2a2a',
                      borderColor: '#3e3e3e',
                      minHeight: '40px',
                      height: '40px',
                      boxShadow: 'none',
                      borderRadius: '4px',
                      '&:hover': {
                        borderColor: '#4a4a4a'
                      }
                    }),
                    menu: (base) => ({
                      ...base,
                      backgroundColor: '#2a2a2a',
                      zIndex: 9999,
                    }),
                    menuList: (base) => ({
                      ...base,
                      maxHeight: '150px',
                      padding: '4px 0'
                    }),
                    option: (base, { isFocused, isSelected }) => ({
                      ...base,
                      backgroundColor: getOptionBackgroundColor({ isFocused, isSelected }),
                      color: '#ffffff',
                      '&:active': {
                        backgroundColor: '#3e3e3e'
                      },
                      fontFamily: 'Montserrat, Arial, Helvetica, sans-serif',
                      fontSize: '10px'
                    }),
                    input: (base) => ({
                      ...base,
                      color: '#ffffff',
                      margin: '0',
                      padding: '0',
                      fontFamily: 'Montserrat, Arial, Helvetica, sans-serif',
                      fontSize: '10px'
                    }),
                    placeholder: (base) => ({
                      ...base,
                      color: '#757575',
                      fontFamily: 'Montserrat, Arial, Helvetica, sans-serif',
                      fontSize: '10px'
                    }),
                    singleValue: (base) => ({
                      ...base,
                      color: '#ffffff',
                      fontFamily: 'Montserrat, Arial, Helvetica, sans-serif',
                      fontSize: '10px'
                    }),
                    indicatorSeparator: (base) => ({
                      ...base,
                      backgroundColor: '#3e3e3e'
                    }),
                    dropdownIndicator: (base) => ({
                      ...base,
                      color: '#757575',
                      padding: '8px',
                      '&:hover': {
                        color: '#ffffff'
                      },
                      fontSize: '10px'
                    }),
                    clearIndicator: (base) => ({
                      ...base,
                      color: '#757575',
                      padding: '8px',
                      '&:hover': {
                        color: '#ffffff'
                      },
                      fontSize: '10px'
                    }),
                    valueContainer: (base) => ({
                      ...base,
                      padding: '0 8px',
                      fontSize: '10px'
                    })
                  }}
                />
              </div>
            </Margin>
            <Margin bottom={1} css={styles.row}>
              <Text as="label" size={11} style={{ width: '100px', textAlign: 'left' }}>
                Condition
              </Text>
              <div style={{ width: '180px', marginLeft: '2px' }}>
                <Select
                  options={CONDITION_OPTIONS}
                  getOptionLabel={getOptionLabel}
                  getOptionValue={getOptionValue}
                  isDisabled={disabled}
                  placeholder="Condition"
                  onChange={(selectedOption) => {
                    form.fields.condition.onChange({ target: { value: selectedOption } });
                  }}
                  value={form.fields.condition.value}
                  styles={{
                    container: (base) => ({
                      ...base,
                      width: '100%',
                    }),
                    control: (base) => ({
                      ...base,
                      backgroundColor: '#2a2a2a',
                      borderColor: '#3e3e3e',
                      minHeight: '40px',
                      height: '40px',
                      boxShadow: 'none',
                      borderRadius: '4px',
                      '&:hover': {
                        borderColor: '#4a4a4a'
                      }
                    }),
                    menu: (base) => ({
                      ...base,
                      backgroundColor: '#2a2a2a',
                      zIndex: 9999,
                    }),
                    menuList: (base) => ({
                      ...base,
                      maxHeight: '150px',
                      padding: '4px 0'
                    }),
                    option: (base, { isFocused, isSelected }) => ({
                      ...base,
                      backgroundColor: getOptionBackgroundColor({ isFocused, isSelected }),
                      color: '#ffffff',
                      '&:active': {
                        backgroundColor: '#3e3e3e'
                      },
                      fontFamily: 'Montserrat, Arial, Helvetica, sans-serif',
                      fontSize: '10px'
                    }),
                    input: (base) => ({
                      ...base,
                      color: '#ffffff',
                      margin: '0',
                      padding: '0',
                      fontFamily: 'Montserrat, Arial, Helvetica, sans-serif',
                      fontSize: '10px'
                    }),
                    placeholder: (base) => ({
                      ...base,
                      color: '#757575',
                      fontFamily: 'Montserrat, Arial, Helvetica, sans-serif',
                      fontSize: '10px'
                    }),
                    singleValue: (base) => ({
                      ...base,
                      color: '#ffffff',
                      fontFamily: 'Montserrat, Arial, Helvetica, sans-serif',
                      fontSize: '10px'
                    }),
                    indicatorSeparator: (base) => ({
                      ...base,
                      backgroundColor: '#3e3e3e'
                    }),
                    dropdownIndicator: (base) => ({
                      ...base,
                      color: '#757575',
                      padding: '8px',
                      '&:hover': {
                        color: '#ffffff'
                      },
                      fontSize: '10px'
                    }),
                    clearIndicator: (base) => ({
                      ...base,
                      color: '#757575',
                      padding: '8px',
                      '&:hover': {
                        color: '#ffffff'
                      },
                      fontSize: '10px'
                    }),
                    valueContainer: (base) => ({
                      ...base,
                      padding: '0 8px',
                      fontSize: '10px'
                    })
                  }}
                />
              </div>
            </Margin>
            <Margin bottom={1} css={styles.row}>
              <Text as="label" size={11} style={{ width: '100px', textAlign: 'left' }}>
                Binary Winner
              </Text>
              <div style={{ width: '180px', position: 'relative', zIndex: 5, marginLeft: '2px' }}>
                <Select
                  key={`binary-winner-select-${renderKey}-${eventTeams.length}`}
                  inputId="binary-winner-select"
                  name="binary-winner-select"
                  classNamePrefix="binary-winner"
                  options={eventTeams.length > 0 ? eventTeams : TEAM_OPTIONS}
                  getOptionLabel={getTeamLabel}
                  getOptionValue={getTeamValue}
                  isDisabled={disabled}
                  placeholder={eventSelection ? "Select winner..." : "Select event first..."}
                  onChange={handleBinaryWinnerChange}
                  value={selectedBinaryWinner}
                  // Make sure to apply a good z-index and attach menu to body
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                  menuPlacement="bottom"
                  menuShouldScrollIntoView={false}
                  menuShouldBlockScroll={false}
                  // Apply consistent styles
                  styles={{
                    container: (base) => ({
                      ...base,
                      width: '100%',
                    }),
                    control: (base) => ({
                      ...base,
                      backgroundColor: '#2a2a2a',
                      borderColor: '#3e3e3e',
                      minHeight: '40px',
                      height: '40px',
                      boxShadow: 'none',
                      borderRadius: '4px',
                      '&:hover': {
                        borderColor: '#4a4a4a'
                      }
                    }),
                    menuPortal: (base) => ({
                      ...base,
                      zIndex: 9999,
                    }),
                    menu: (base) => ({
                      ...base,
                      backgroundColor: '#2a2a2a',
                      zIndex: 9999,
                    }),
                    menuList: (base) => ({
                      ...base,
                      maxHeight: '150px',
                      padding: '4px 0'
                    }),
                    option: (base, { isFocused, isSelected }) => ({
                      ...base,
                      backgroundColor: getOptionBackgroundColor({ isFocused, isSelected }),
                      color: '#ffffff',
                      '&:active': {
                        backgroundColor: '#3e3e3e'
                      },
                      fontFamily: 'Montserrat, Arial, Helvetica, sans-serif',
                      fontSize: '10px'
                    }),
                    input: (base) => ({
                      ...base,
                      color: '#ffffff',
                      margin: '0',
                      padding: '0',
                      fontFamily: 'Montserrat, Arial, Helvetica, sans-serif',
                      fontSize: '10px'
                    }),
                    placeholder: (base) => ({
                      ...base,
                      color: '#757575',
                      fontFamily: 'Montserrat, Arial, Helvetica, sans-serif',
                      fontSize: '10px'
                    }),
                    singleValue: (base) => ({
                      ...base,
                      color: '#ffffff',
                      fontFamily: 'Montserrat, Arial, Helvetica, sans-serif',
                      fontSize: '10px'
                    }),
                    indicatorSeparator: (base) => ({
                      ...base,
                      backgroundColor: '#3e3e3e'
                    }),
                    dropdownIndicator: (base) => ({
                      ...base,
                      color: '#757575',
                      padding: '8px',
                      '&:hover': {
                        color: '#ffffff'
                      },
                      fontSize: '10px'
                    }),
                    clearIndicator: (base) => ({
                      ...base,
                      color: '#757575',
                      padding: '8px',
                      '&:hover': {
                        color: '#ffffff'
                      },
                      fontSize: '10px'
                    }),
                    valueContainer: (base) => ({
                      ...base,
                      padding: '0 8px',
                      fontSize: '10px'
                    })
                  }}
                />
              </div>
            </Margin>
            <Margin bottom={1} css={styles.row}>
              <Text as="label" size={11} style={{ width: '100px', textAlign: 'left' }}>
                Outcome
              </Text>
              <div style={{ width: '180px', marginLeft: '2px' }}>
                <FormInput
                  strictLong
                  name="outcome"
                  placeholder="Numeric value"
                  css={styles.control}
                  disabled={disabled}
                  type="number"
                  step="0.000001"
                // Supports BigDecimal values with high precision
                />
              </div>
            </Margin>
            <Margin bottom={1} css={styles.row}>
              <Text as="label" size={11} style={{ width: '100px', textAlign: 'left' }}>
                Description
              </Text>
              <div style={{ width: '180px', marginLeft: '2px' }}>
                <FormInput
                  strictLong
                  name="description"
                  placeholder="Enter description"
                  css={styles.control}
                  disabled={disabled}
                />
              </div>
            </Margin>
          </>
        )
      }}
    </EditableFormPanel>
  )
}

EditTriggerFormComponent.propTypes = {
  form: PropTypes.shape(),
  header: PropTypes.string.isRequired,
  getOptionLabel: PropTypes.func,
  getOptionValue: PropTypes.func,
  getEventLabel: PropTypes.func,
  getEventValue: PropTypes.func,
  getAthleteLabel: PropTypes.func,
  getAthleteValue: PropTypes.func,
  loadEvents: PropTypes.func,
  loadAthletes: PropTypes.func,
  loadTriggerConditions: PropTypes.func,
  loadTriggerCategories: PropTypes.func,
  loadTriggerSubcategories: PropTypes.func,
  loadTournaments: PropTypes.func,
  onChangeTournament: PropTypes.func,
  onChangeEvent: PropTypes.func,
  onChangeAthlete: PropTypes.func,
  onSubmitRequest: PropTypes.func,
}

export default EditTriggerFormComponent
